import { PlusOutlined } from '@ant-design/icons';
import { useThemeContext } from '@nxsys/context/AppContextProvider/ThemeContextProvider';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { Icons } from '@nxsys/types/icons';
import { Option } from '@nxsys/types/models/Common';
import {
  calculateHolidayBalanceBySubcontractor,
  isHolidayPayRate,
  showCurrencyByCountry,
} from '@nxsys/utils';
import { Descriptions, Divider, Form, FormInstance, FormProps } from 'antd';
import { useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import AppButton from '../AppButton';
import AppCard from '../AppCard';
import AppFormContainer, { AppFormContainerProps } from '../AppFormContainer';
import AppIconButton from '../AppIconButton';
import AppInput from '../AppInput';
import AppSelectSync from '../AppSelectSync';
import AppTableContainer from '../AppTableContainer';
import { isEmpty, some } from 'lodash';
import {
  CODE_RATE_HOLIDAY,
  CODE_RATE_HOLIDAY_LEAVER,
  HP_CODES,
  IGNORE_CODES,
  LEAVER_CODES,
} from '@nxsys/constants/AppConst';
import { PAYMENT_MODEL_ENUM } from '@nxsys/constants/AppConstants';
import { HolidayMethodEnum } from '@nxsys/constants/AppEnums';
import { formatMoney } from '@nxsys/utils/number-helper';
import AppDescription from '@nxsys/components/AppDescription';
import AppIconCard from '../AppIconCard';
import AppIconCurrency from '../AppIconCurrency';
import AppCurrencyInput from '@nxsys/components/AppCurrencyInput';
import AppRateInput from '@nxsys/components/AppRateInput';

// Rate codes that draw from the Holiday Pay balance (normal + leaver).
// Employer-costs leaver (PRHPERL) uses a separate balance, so it is excluded.
const HP_BALANCE_CODES = [CODE_RATE_HOLIDAY, CODE_RATE_HOLIDAY_LEAVER];

const sumHolidayPay = (rates: any[]) =>
  (rates ?? []).reduce((sum: number, r: any) => {
    const code = r?.rateCode?.value ?? r?.rateCode?.fullData?.code;
    if (!HP_BALANCE_CODES.includes(code)) return sum;
    const amount = Number(r?.rate);
    return sum + (isNaN(amount) ? 0 : amount);
  }, 0);

// Round to currency precision (2dp) before comparing so floating-point
// noise (e.g. 10.000000000000002 from opening + accrued - paid) does not
// flag an equal holiday-pay amount as exceeding the balance.
const roundMoney = (value: number) =>
  Math.round((Number(value) + Number.EPSILON) * 100) / 100;

type AppRateFormProps = Omit<AppFormContainerProps, 'children'> & {
  className?: string;
  form?: FormInstance<any>;
  initialValues?: any;
  onFinish?: (values: any) => void;
  onFinishFailed?: (errorInfo: any) => void;
  listDisabled?: (keyof AppRateFormItem)[];
  listHidden?: (keyof AppRateFormItem)[];
  disabledDelete?: boolean;
  disabledAdd?: boolean;
  hideDelete?: boolean;
  hideAdd?: boolean;
  subcontractor?: any;
  isInvoice?: boolean;
  agency?: any;
  company?: any;
  hasBorder?: boolean;
};

export type AppRateFormItem = {
  description: string;
  net: string;
  rate: string;
  rateId: string;
  rateCode: Option<string> | null;
  rateType: string;
  total: string;
  units: string;
  vatDetails: Option<string> | null;
  vatRate: string;
  // VAT code letter (e.g. "S", "E") submitted alongside vatDetails.
  vatCode?: string;
};

export type AppRateFormValues = {
  rates: Array<AppRateFormItem>;
};

export const PAYMENT_RATE_DEFAULT = {
  description: '',
  net: '',
  rate: '',
  rateId: '',
  rateCode: null,
  rateType: 'RATE',
  total: '',
  units: '',
  vatDetails: null,
  vatRate: '',
  vatCode: '',
};

export const calculateNetAndTotalFromRate = (
  rate: string,
  units: string,
  vatRate: string,
): { net: string; total: string } => {
  if (!rate || !units || !vatRate) return { net: '0.00', total: '0.00' };
  const net = (parseFloat(rate) * parseFloat(units)).toFixed(2);
  const total = (parseFloat(net) * (1 + parseFloat(vatRate) / 100)).toFixed(2);
  return { net, total };
};

const initialValues: AppRateFormValues = {
  rates: [PAYMENT_RATE_DEFAULT],
};

const AppRateForm = ({
  className,
  form: formProp,
  initialValues: initialValuesProp,
  onFinish: onFinishProp,
  onFinishFailed: onFinishFailedProp,
  listDisabled = [],
  listHidden = [],
  disabledDelete = false,
  disabledAdd = false,
  hideDelete = false,
  hideAdd = false,
  subcontractor,
  isInvoice = false,
  agency,
  company,
  hasBorder = true,
}: AppRateFormProps) => {
  const { messages } = useTypedIntl();
  const { theme } = useThemeContext();

  const { payrollCountry, isBureau } = useSelector(
    (state: AppState) => state.appStore,
  );

  const [form] = Form.useForm<AppRateFormValues>();
  const finalForm = formProp ?? form;

  const finalInitialValues = initialValuesProp ?? initialValues;

  const watchRates = Form.useWatch<AppRateFormItem[]>('rates', finalForm);

  const addRateRef = useRef<((defaultValue?: any) => void) | null>(null);

  // Helper function to calculate net and total values
  const calculateValues = (fieldName: number) => {
    const rate = finalForm.getFieldValue(['rates', fieldName, 'rate']) || 0;
    const units = finalForm.getFieldValue(['rates', fieldName, 'units']) || 0;
    const vatRate =
      finalForm.getFieldValue(['rates', fieldName, 'vatRate']) || 0;

    const { net, total } = calculateNetAndTotalFromRate(rate, units, vatRate);

    // Update the form with calculated values
    finalForm.setFieldValue(['rates', fieldName, 'net'], net);
    finalForm.setFieldValue(['rates', fieldName, 'total'], total);
  };

  const onFinish: FormProps<AppRateFormValues>['onFinish'] = (values) => {
    console.trace('values', values);
  };
  const finalOnFinish = onFinishProp ?? onFinish;

  const onFinishFailed: FormProps<AppRateFormValues>['onFinishFailed'] = (
    errorInfo,
  ) => {
    console.trace('Failed:', errorInfo);
  };
  const finalOnFinishFailed = onFinishFailedProp ?? onFinishFailed;

  const holidayBalance = useMemo(
    () => calculateHolidayBalanceBySubcontractor(subcontractor),
    [subcontractor],
  );
  const holidayPayMethod = useMemo(
    () =>
      subcontractor?.setting?.holidayPayMethod ??
      subcontractor?.primaryAgency?.holidayPayMethod ??
      subcontractor?.companyPayment?.holidayPayMethod,
    [subcontractor],
  );

  const hasHpRate = some(watchRates, (e) => {
    const val = e?.rateCode?.value ?? '';
    const fullCode = e?.rateCode?.fullData?.code ?? '';
    return HP_CODES.includes(val) || HP_CODES.includes(fullCode);
  });

  const needToShowHoliday = hasHpRate;

  // Total of rates drawing from the Holiday Pay balance (normal + leaver).
  // Employer-costs leaver (PRHPERL) uses a separate balance and is excluded.
  const totalHolidayPay = useMemo(
    () => sumHolidayPay(watchRates),
    [watchRates],
  );
  const isHolidayBalanceExceeded =
    !!subcontractor &&
    roundMoney(totalHolidayPay) > roundMoney(holidayBalance ?? 0);

  // Validator for a rate row's amount: blocks Save (marks the form invalid)
  // when the total holiday pay across rows exceeds the balance. No inline
  // message — the descriptive text is shown under the Holiday Pay Balance.
  const validateHolidayBalance = (fieldName: number) => (): Promise<void> => {
    const rateCode = finalForm.getFieldValue([
      'rates',
      fieldName,
      'rateCode',
    ])?.value;
    // Leaver holiday pay (PRHPL) is read-only, so it is not validated against
    // the balance — only the editable normal holiday pay (PRHP) is.
    if (!subcontractor || rateCode !== CODE_RATE_HOLIDAY) {
      return Promise.resolve();
    }
    return roundMoney(sumHolidayPay(finalForm.getFieldValue('rates'))) >
      roundMoney(holidayBalance ?? 0)
      ? Promise.reject(new Error(''))
      : Promise.resolve();
  };

  const needToShow52WeekAverageHourRate =
    hasHpRate &&
    subcontractor?.paymentModel === PAYMENT_MODEL_ENUM.PAYEE &&
    holidayPayMethod === HolidayMethodEnum.FIFTY_TWO_WEEK_AVERAGE;

  const activePayeeUmbrella =
    ['PAYEE', 'UMBRELLA'].includes(subcontractor?.setting?.paymentModel) ||
    (isInvoice &&
      (isEmpty(agency?.paymentModel) ||
        some(agency?.paymentModel, (item) =>
          ['PAYEE', 'UMBRELLA'].includes(item),
        )) &&
      some(company?.paymentModel, (item) =>
        ['PAYEE', 'UMBRELLA'].includes(item),
      ));

  return (
    <AppFormContainer
      className={className}
      form={finalForm}
      initialValues={finalInitialValues}
      onFinish={finalOnFinish}
      onFinishFailed={finalOnFinishFailed}
      wrapperCol={{ span: 24 }}
    >
      {(needToShowHoliday || needToShow52WeekAverageHourRate) && (
        <AppCard className="mb-4 p-4">
          <AppDescription>
            {needToShowHoliday && (
              <Descriptions.Item label={messages['common.holidayPayBalance']}>
                {formatMoney(
                  holidayBalance ?? 0,
                  showCurrencyByCountry(payrollCountry),
                )}
              </Descriptions.Item>
            )}
            {needToShow52WeekAverageHourRate && (
              <>
                <Descriptions.Item label={messages['common.averageHour']}>
                  {formatMoney(
                    subcontractor?.average52WeekData?.averageHours ?? 0,
                    showCurrencyByCountry(payrollCountry),
                  )}
                </Descriptions.Item>
                <Descriptions.Item label={messages['common.averageHourlyRate']}>
                  {formatMoney(
                    subcontractor?.average52WeekData?.averageHourlyRate ?? 0,
                    showCurrencyByCountry(payrollCountry),
                  )}
                </Descriptions.Item>
              </>
            )}
          </AppDescription>
          {needToShowHoliday && isHolidayBalanceExceeded && (
            <div className="text-red-500 text-[12px] mt-1">
              {messages['common.holidayPayBalanceNotEnough'] ??
                'Holiday pay balance is not enough'}
            </div>
          )}
        </AppCard>
      )}
      <AppCard hasBorder={hasBorder}>
        <div className="flex items-center justify-between p-[16px]">
          <AppIconCard
            icon={<AppIconCurrency />}
            title={messages['common.rates']}
          />
          {!hideAdd && (
            <AppButton
              leftIcon={<PlusOutlined />}
              name={messages['common.addNewRateCode']}
              variant="outlined"
              onClick={() => addRateRef.current?.({ rateType: 'RATE' })}
              disabled={disabledAdd}
            />
          )}
        </div>
        <Divider className="my-0" />
        <Form.List name="rates">
          {(fields, { add, remove }) => {
            // Store the add function in ref for external access
            addRateRef.current = add;
            return (
              <div>
                <AppTableContainer
                  data={fields}
                  rowKey="key"
                  pagination={false}
                  loading={false}
                  page={0}
                  total={fields.length}
                  pageSize={fields.length}
                  scroll={{ x: 1500, y: 700 }}
                  columns={[
                    {
                      key: 'rateCode',
                      title: messages['assignment.rate.rateCode'],
                      dataIndex: 'rateCode',
                      hidden: listHidden.includes('rateCode'),
                      render: (_: any, field: any) => {
                        const description = finalForm.getFieldValue([
                          'rates',
                          field.name,
                          'description',
                        ]);
                        const vatDetails = finalForm.getFieldValue([
                          'rates',
                          field.name,
                          'vatDetails',
                        ]);
                        const units = finalForm.getFieldValue([
                          'rates',
                          field.name,
                          'units',
                        ]);
                        return (
                          <Form.Item
                            name={[field.name, 'rateCode']}
                            rules={[
                              {
                                required: true,
                                message: messages['form.pleaseSelect'],
                              },
                            ]}
                            className="mb-0"
                          >
                            <AppSelectSync
                              type="paymentRate"
                              apiType="searchPaymentRate"
                              popupMatchSelectWidth={false}
                              placeholder={
                                messages['assignment.rate.selectRateCode']
                              }
                              paramFilter={
                                isBureau
                                  ? {
                                      isBureau: true,
                                      includeInPayeCalculation: true,
                                      subcontractorRef: subcontractor?.id,
                                      companyRef: company?.id,
                                    }
                                  : {
                                      subcontractorRef: subcontractor?.id,
                                      companyRef: company?.id,
                                      ignoreCodes: !activePayeeUmbrella
                                        ? IGNORE_CODES
                                        : LEAVER_CODES,
                                      subcontractorPaymentModel:
                                        subcontractor?.setting?.paymentModel,
                                      agencyPaymentModel: agency?.paymentModel,
                                      companyPaymentModel:
                                        company?.paymentModel,
                                      isStatutory: !activePayeeUmbrella
                                        ? false
                                        : undefined,
                                    }
                              }
                              onChangeValue={(value: any) => {
                                console.log('value', value);
                                const rateCode = value.fullData?.code ?? value.label;
                                finalForm.setFieldValue(
                                  ['rates', field.name, 'rateCode'],
                                  { ...value, value: rateCode },
                                );
                                finalForm.setFieldValue(
                                  ['rates', field.name, 'rateId'],
                                  value?.value ?? '',
                                );

                                if (!description) {
                                  finalForm.setFieldValue(
                                    ['rates', field.name, 'description'],
                                    value?.fullData?.description ?? '',
                                  );
                                }
                                if (!vatDetails) {
                                  const vatDetailValue = `${value?.fullData?.defaultVATCode?.description} (${value?.fullData?.defaultVATCode?.VATCode}) - ${value?.fullData?.defaultVATCode?.VATRate}`;
                                  finalForm.setFieldValue(
                                    ['rates', field.name, 'vatDetails'],
                                    {
                                      label: vatDetailValue ?? '',
                                      value: vatDetailValue ?? '',
                                    },
                                  );
                                  finalForm.setFieldValue(
                                    ['rates', field.name, 'vatRate'],
                                    value?.fullData?.defaultVATCode?.VATRate,
                                  );
                                  finalForm.setFieldValue(
                                    ['rates', field.name, 'vatCode'],
                                    value?.fullData?.defaultVATCode?.VATCode ??
                                      '',
                                  );
                                }
                                finalForm.setFieldValue(
                                  ['rates', field.name, 'units'],
                                  isHolidayPayRate(rateCode)
                                    ? 1
                                    : (units ?? ''),
                                );
                                // Trigger calculation when rate code changes
                                setTimeout(
                                  () => calculateValues(field.name),
                                  100,
                                );
                              }}
                              disabled={listDisabled.includes('rateCode')}
                            />
                          </Form.Item>
                        );
                      },
                      width: 230,
                    },
                    {
                      key: 'rateType',
                      title: messages['assignment.rate.type'],
                      dataIndex: 'rateType',
                      hidden: listHidden.includes('rateType'),
                      render: () => <AppInput value="RATE" disabled={true} />,
                    },
                    {
                      key: 'description',
                      title: messages['assignment.rate.description'],
                      dataIndex: 'description',
                      hidden: listHidden.includes('description'),
                      render: (_: any, field: any) => (
                        <Form.Item
                          name={[field.name, 'description']}
                          className="mb-0"
                        >
                          <AppInput
                            placeholder={
                              messages['assignment.rate.enterDescription']
                            }
                            disabled={listDisabled.includes('description')}
                          />
                        </Form.Item>
                      ),
                      width: 220,
                    },
                    {
                      key: 'rate',
                      title: messages['assignment.rate.rate'],
                      dataIndex: 'rate',
                      hidden: listHidden.includes('rate'),
                      render: (_: any, field: any) => {
                        const rateCode = finalForm.getFieldValue([
                          'rates',
                          field.name,
                          'rateCode',
                        ])?.value;
                        // Leaver rates are paid out at fixed, system-derived
                        // amounts, so the rate is read-only and not validated
                        // against the balance.
                        const isLeaverRate = LEAVER_CODES.includes(rateCode);
                        return (
                          <Form.Item
                            name={[field.name, 'rate']}
                            rules={[
                              {
                                required: true,
                                message: messages['form.pleaseEnter'],
                              },
                              {
                                validator: validateHolidayBalance(field.name),
                              },
                            ]}
                            dependencies={[['rates', field.name, 'rateCode']]}
                            className="mb-0"
                          >
                            <AppCurrencyInput
                              prefix={showCurrencyByCountry(payrollCountry)}
                              placeholder={
                                messages['assignment.rate.enterRate']
                              }
                              onChange={() => {
                                // Trigger calculation when rate changes
                                setTimeout(
                                  () => calculateValues(field.name),
                                  100,
                                );
                              }}
                              disabled={
                                listDisabled.includes('rate') || isLeaverRate
                              }
                            />
                          </Form.Item>
                        );
                      },
                    },
                    {
                      key: 'units',
                      title: messages['assignment.rate.units'],
                      dataIndex: 'units',
                      hidden: listHidden.includes('units'),
                      render: (_: any, field: any) => {
                        const rateCode = finalForm.getFieldValue([
                          'rates',
                          field.name,
                          'rateCode',
                        ])?.value;

                        return (
                          <Form.Item
                            name={[field.name, 'units']}
                            rules={[
                              {
                                required: !isHolidayPayRate(rateCode),
                                message: messages['form.pleaseEnter'],
                              },
                            ]}
                            className="mb-0"
                          >
                            <AppRateInput
                              placeholder="Enter units"
                              onChange={() => {
                                // Trigger calculation when units change
                                setTimeout(
                                  () => calculateValues(field.name),
                                  100,
                                );
                              }}
                              disabled={
                                listDisabled.includes('units') ||
                                isHolidayPayRate(rateCode)
                              }
                            />
                          </Form.Item>
                        );
                      },
                    },
                    {
                      key: 'net',
                      title: messages['assignment.rate.net'],
                      dataIndex: 'net',
                      hidden: listHidden.includes('net'),
                      render: (_: any, field: any) => {
                        const netValue =
                          finalForm.getFieldValue([
                            'rates',
                            field.name,
                            'net',
                          ]) || '0.00';
                        return (
                          <div style={{ padding: '8px 0' }}>
                            {formatMoney(
                              parseFloat(netValue),
                              showCurrencyByCountry(payrollCountry),
                            )}
                          </div>
                        );
                      },
                      width: 150,
                    },
                    {
                      key: 'vatRate',
                      title: messages['assignment.rate.vatRate'],
                      dataIndex: 'vatRate',
                      hidden: listHidden.includes('vatRate'),
                      render: (_: any, field: any) => (
                        <Form.Item
                          name={[field.name, 'vatDetails']}
                          rules={[
                            {
                              required: true,
                              message: messages['form.pleaseSelect'],
                            },
                          ]}
                          className="mb-0"
                        >
                          <AppSelectSync
                            type="VATCode"
                            apiType="searchVATCode"
                            placeholder={
                              messages['assignment.rate.selectVatRate']
                            }
                            style={{ width: '100%' }}
                            onChangeValue={(value: any) => {
                              finalForm.setFieldValue(
                                ['rates', field.name, 'vatDetails'],
                                {
                                  ...value,
                                  // Store the display string as value (matches
                                  // existing rates + rate-code auto-fill).
                                  // Submit sends vatDetails.value and the
                                  // backend round-trips this string, NOT the
                                  // VAT code id.
                                  value: value?.label ?? '',
                                },
                              );
                              finalForm.setFieldValue(
                                ['rates', field.name, 'vatRate'],
                                value?.fullData?.VATRate,
                              );
                              finalForm.setFieldValue(
                                ['rates', field.name, 'vatCode'],
                                value?.fullData?.VATCode ?? '',
                              );
                              // Trigger calculation when VAT rate changes
                              setTimeout(
                                () => calculateValues(field.name),
                                100,
                              );
                            }}
                            disabled={
                              listDisabled.includes('vatDetails') ||
                              listDisabled.includes('vatRate')
                            }
                          />
                        </Form.Item>
                      ),
                      width: 230,
                    },
                    {
                      key: 'total',
                      title: messages['assignment.rate.total'],
                      dataIndex: 'total',
                      hidden: listHidden.includes('total'),
                      render: (_: any, field: any) => {
                        const totalValue =
                          finalForm.getFieldValue([
                            'rates',
                            field.name,
                            'total',
                          ]) || '0.00';
                        return (
                          <div style={{ padding: '8px 0' }}>
                            {formatMoney(
                              parseFloat(totalValue),
                              showCurrencyByCountry(payrollCountry),
                            )}
                          </div>
                        );
                      },
                      width: 150,
                    },
                    ...(!hideDelete
                      ? [
                          {
                            key: 'actions',
                            title: ' ',
                            dataIndex: 'actions',
                            width: 65,
                            fixed: 'right' as const,
                            align: 'center' as const,
                            render: (_: any, field: any) => {
                              const deleteDisabled =
                                watchRates?.length === 1 ||
                                disabledDelete;
                              return (
                                <AppIconButton
                                  icon={
                                    <Icons.IconDelete
                                      color={
                                        deleteDisabled
                                          ? theme.palette.gray[500]
                                          : theme.palette.red.ERROR500
                                      }
                                    />
                                  }
                                  disabled={deleteDisabled}
                                  onClick={() => {
                                    if (deleteDisabled) return;
                                    remove(field.name);
                                  }}
                                />
                              );
                            },
                          },
                        ]
                      : []),
                  ]}
                  titleEmpty={messages['common.rate.empty']}
                  virtual
                />
              </div>
            );
          }}
        </Form.List>
      </AppCard>
    </AppFormContainer>
  );
};

export default AppRateForm;
